<script lang="ts" setup>
import { defineRule, Field as VeeField } from 'vee-validate'

import { notActiveGift } from '@/utils/rulesOfValidate'

import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useBasketStore } from '@/store/basket/basket'

import { GiftPayload } from '@/api/modules/basket/types'
import { Success } from '@/api/results'
import { Gift } from '@/types/api/api-v3/basket'

import BasketGiftsInfo from '@/components/Basket/basket-gifts/BasketGiftsInfo.vue'
import BasketGiftsList from '@/components/Basket/basket-gifts/BasketGiftsList.vue'
import BasketGiftsToggle from '@/components/Basket/basket-gifts/BasketGiftsToggle.vue'
import BasketSection from '@/components/Basket/basket-utils/BasketSection.vue'
import BasketTitle from '@/components/Basket/basket-utils/BasketTitle.vue'

const NOT_ACTIVE_GIFT = 'notActiveGift'
const FAILURE_MESSAGE = 'Что-то пошло не так'
const ADDED_GIFT_MESSAGE = 'Подарок добавлен в корзину'
const DELETED_GIFT_MESSAGE = 'Подарок удален из корзины'

type Props = {
  gifts: Gift[]
}

defineProps<Props>()

const basketStore = useBasketStore()
const { showSnackbar } = useAppStore()

const isGiftNeed = defineModel<boolean>()

const selectedGift = ref<GiftPayload | null>(null)
const isLoading = ref<boolean>(false)

const rule = computed(() => (isGiftNeed.value ? NOT_ACTIVE_GIFT : ''))

// Метод для удобной установки лоадинга блока подарков
const setLoading = (state: boolean) => (isLoading.value = state)

// Метод обработки удаления/добавления подарка в корзину
const handleGiftResult = (result: unknown, successMessage: string) => {
  if (!(result instanceof Success)) {
    showSnackbar({ message: FAILURE_MESSAGE })
    setLoading(false)

    return false
  }

  showSnackbar({ message: successMessage, type: SnackbarType.SUCCESS })
  setLoading(false)

  return true
}

// Функция для обработки выбранного подарка
const onSelectGift = async (gift: GiftPayload) => {
  setLoading(true)

  // Сначала удаляем уже выбранный подарок
  if (selectedGift.value) {
    await basketStore.deleteGift({
      gift_id_list: [selectedGift.value.product_id],
    })
  }

  const result = await basketStore.setGift(gift)

  const isGiftHandled = handleGiftResult(result, ADDED_GIFT_MESSAGE)

  if (isGiftHandled) {
    isGiftNeed.value = true
    selectedGift.value = gift
  }
}

// Функция для переключения нужен/не нужен подарок
const updateModelValue = async (value: boolean) => {
  // Если включили тагл и уже есть выбранный подарок
  if (value && basketStore.getSelectedGift) {
    selectedGift.value = basketStore.getSelectedGift
  }

  // Если пользователь не добавлял подарок при включенном тагле - просто выключаем его и выходим
  if (value || !selectedGift.value) {
    isGiftNeed.value = value

    return
  }

  setLoading(true)

  const selectedGiftProductId = selectedGift.value?.product_id

  if (!selectedGiftProductId) {
    setLoading(false)
    isGiftNeed.value = value

    return
  }

  const result = await basketStore.deleteGift({
    gift_id_list: [selectedGiftProductId],
  })

  const isGiftHandled = handleGiftResult(result, DELETED_GIFT_MESSAGE)

  if (isGiftHandled) {
    isGiftNeed.value = value
    selectedGift.value = null
  }
}

onMounted(() => {
  // Получаем текущий выбранный подарок пользователем
  const selectedBasketGift = basketStore.getSelectedGift

  if (!selectedBasketGift) return

  // Устанавливаем подарок как текущий выбранный в компоненте и переключаем тагл на "Нужен подарок"
  selectedGift.value = selectedBasketGift
  isGiftNeed.value = true
})

defineRule(NOT_ACTIVE_GIFT, notActiveGift)
</script>

<template>
  <basket-section id="gifts" class="gifts" :loading="isLoading">
    <basket-title title="Подарки" />

    <vee-field
      v-slot="{ errors }"
      name="gifts"
      vid="gifts"
      :rules="rule"
      :model-value="selectedGift"
    >
      <basket-gifts-list
        v-for="gift in gifts"
        :key="gift.rule_id"
        :products="gift.products"
        :rule-id="gift.rule_id"
        :is-gift-need="isGiftNeed"
        :selected-gift="selectedGift"
        @select-gift="onSelectGift"
      />

      <basket-gifts-info :errors="errors" />
    </vee-field>

    <hr class="gifts__line" />

    <basket-gifts-toggle
      :is-gift-need="isGiftNeed"
      @update:model-value="updateModelValue"
    />
  </basket-section>
</template>

<style lang="scss" scoped>
.gifts {
  overflow: hidden;
}

.gifts__line {
  margin: 20px;
  border: 1px solid var(--ui-grey);

  @include media('<tablet') {
    margin: 16px 0 20px;
  }
}
</style>
